// @ts-nocheck

import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { Target } from '../../../../client';
import ArrowButton from '../../../../components/ArrowButton';
import Swipe from '../../../../components/Swipe';
import SwipePagination from '../../../../components/SwipePagination';
import { type Licence } from '../../../../types/common';
import { useMPFamilyContext } from '../../contexts/MPFamilyContext';
import { type MPCardStatus } from '../../types/cards';

import MPCardDetailsHeader from './MPCardDetailsHeader';

const CARDS_GAP = 0.6; // em
const NEXT_CARD_SHOW_WIDTH = 2; //em

const SwipeCardsWrapper = styled.div`
  overflow: hidden;
`;

const StyledSwipe = styled(Swipe)`
  display: flex;
  align-items: flex-end;
  width: calc(100% - ${NEXT_CARD_SHOW_WIDTH}em);
  opacity: 1;

  & > div {
    padding: 0;
    padding-right: ${CARDS_GAP}em;
  }
`;

const StyledMPCardHeader = styled(MPCardDetailsHeader)`
  width: 100%;
`;

const SwipePaginationWrapper = styled.div`
  margin: 0.6em 0 1.25em;
  position: relative;
`;

const StyledSwipePagination = styled(SwipePagination)`
  --swipe-background-color: #14171fb3;

  text-align: center;

  & > div {
    display: inline-flex;
  }
`;

const PaginationWrapper = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  width: auto;
  display: flex;
  flex-direction: row;
  font-size: 100%;
`;

const PrevButton = styled(ArrowButton)``;
const NextButton = styled(ArrowButton)`
  margin-left: 0.5em;
`;

type SwipeCardsProps = {
  className?: string;
  licence: Licence;
  cards: Array<{
    status: MPCardStatus;
  }>;
  onChangeCard: (index: number) => void;
  indexToSwipe: number;
};

const SwipeCards = ({ className = '', cards, licence, onChangeCard, indexToSwipe }: SwipeCardsProps) => {
  const [swipeIndex, setSwipeIndex] = useState(0);
  const { familyId } = useMPFamilyContext();

  const onSwipeLoaded = useCallback(
    (loaded) => {
      if (loaded) {
        setSwipeIndex(indexToSwipe);
      }
    },
    [indexToSwipe],
  );

  useEffect(() => {
    onChangeCard(swipeIndex);
  }, [onChangeCard, swipeIndex]);

  const onPrevClick = useCallback(() => {
    if (swipeIndex > 0) {
      setSwipeIndex(swipeIndex - 1);
    }
  }, [swipeIndex]);

  const onNextClick = useCallback(() => {
    if (swipeIndex < cards.length) {
      setSwipeIndex(swipeIndex + 1);
    }
  }, [cards.length, swipeIndex]);

  return (
    <SwipeCardsWrapper className={className}>
      <StyledSwipe
        setIndex={setSwipeIndex}
        indexToGo={swipeIndex}
        showNextPage
        noScale
        fullHeight
        alwaysRenderEvery
        alwaysShowEvery
        setIsLoaded={onSwipeLoaded}
        disable={!Target.__MOBILE__}
      >
        {cards.map((card, key) => (
          <StyledMPCardHeader
            key={key}
            card={{ familyId: familyId, index: key }}
            licence={licence}
            status={card.status}
            $item={key}
          />
        ))}
      </StyledSwipe>
      <SwipePaginationWrapper>
        <StyledSwipePagination maxViews={cards.length} currIndex={swipeIndex} />
        {!Target.__MOBILE__ ? (
          <PaginationWrapper>
            <PrevButton angle={180} onClick={onPrevClick} disabled={swipeIndex <= 0} />
            <NextButton angle={0} onClick={onNextClick} disabled={swipeIndex >= cards.length - 1} />
          </PaginationWrapper>
        ) : null}
      </SwipePaginationWrapper>
    </SwipeCardsWrapper>
  );
};

export default SwipeCards;
